// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {EXAMPLE_URL} from '@app/features/app/config/I18nDisplayConstants';
import Translation from '@app/features/messaging/state/Translation';
import ReverseImageSearch from '@app/features/search/state/ReverseImageSearch';
import SearchEngine from '@app/features/search/state/SearchEngine';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Input} from '@app/features/ui/components/form/Input';
import styles from '@app/features/user/components/modals/tabs/chat_settings_tab/SearchEnginesTab.module.css';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useState} from 'react';
export type SearchEngineMode = 'text' | 'image' | 'translate';

interface AddCustomSearchEngineModalProps {
	mode?: SearchEngineMode;
	editingEngineId?: string;
	initialName?: string;
	initialUrlTemplate?: string;
}
export const AddCustomSearchEngineModal: React.FC<AddCustomSearchEngineModalProps> = observer(
	({mode = 'text', editingEngineId, initialName = '', initialUrlTemplate = ''}) => {
		const {t} = useLingui();
		const [name, setName] = useState(initialName);
		const [urlTemplate, setUrlTemplate] = useState(initialUrlTemplate);
		const [error, setError] = useState('');
		const isEditing = editingEngineId != null;
		const isImageMode = mode === 'image';
		const isTranslateMode = mode === 'translate';
		const placeholder = isImageMode ? '{url}' : '{query}';
		const store = isImageMode ? ReverseImageSearch : isTranslateMode ? Translation : SearchEngine;
		const titleAdd = isImageMode
			? t`Add Reverse Image Search Engine`
			: isTranslateMode
				? t`Add Translation Provider`
				: t`Add Search Engine`;
		const titleEdit = isImageMode
			? t`Edit Reverse Image Search Engine`
			: isTranslateMode
				? t`Edit Translation Provider`
				: t`Edit Search Engine`;
		const exampleUrl = isImageMode
			? `${EXAMPLE_URL}/searchbyimage?url=${placeholder}`
			: isTranslateMode
				? `${EXAMPLE_URL}/translate?text=${placeholder}`
				: `${EXAMPLE_URL}/search?q=${placeholder}`;
		const hint = isImageMode
			? t`Use ${'{url}'} where the image URL should be inserted.`
			: isTranslateMode
				? t`Use ${'{query}'} where the text to translate should be inserted.`
				: t`Use ${'{query}'} where the search text should be inserted.`;
		const placeholderRequiredError = isImageMode
			? t`URL pattern must contain ${'{url}'} placeholder.`
			: t`URL pattern must contain ${'{query}'} placeholder.`;
		const handleClose = useCallback(() => {
			ModalCommands.pop();
		}, []);
		const handleSubmit = useCallback(() => {
			const trimmedName = name.trim();
			const trimmedUrl = urlTemplate.trim();
			if (!trimmedName) {
				setError(t`Name is required.`);
				return;
			}
			if (!trimmedUrl) {
				setError(t`URL pattern is required.`);
				return;
			}
			if (!trimmedUrl.includes(placeholder)) {
				setError(placeholderRequiredError);
				return;
			}
			try {
				const testUrl = trimmedUrl.replace(placeholder, 'test');
				new URL(testUrl);
			} catch {
				setError(t`URL pattern must be a valid URL.`);
				return;
			}
			if (isEditing) {
				store.updateCustomEngine(editingEngineId, trimmedName, trimmedUrl);
			} else {
				store.addCustomEngine(trimmedName, trimmedUrl);
			}
			ModalCommands.pop();
		}, [editingEngineId, isEditing, name, placeholder, placeholderRequiredError, store, t, urlTemplate]);
		return (
			<Modal.Root size="small" onClose={handleClose}>
				<Modal.Header title={isEditing ? titleEdit : titleAdd} onClose={handleClose} />
				<Modal.Content>
					<div className={styles.modalForm}>
						<Input
							label={t`Name`}
							value={name}
							onChange={(event) => {
								setName(event.target.value);
								setError('');
							}}
							placeholder={
								isImageMode ? t`My Reverse Image Search` : isTranslateMode ? t`My Translator` : t`My Search Engine`
							}
							autoFocus
						/>
						<Input
							label={t`URL Pattern`}
							value={urlTemplate}
							onChange={(event) => {
								setUrlTemplate(event.target.value);
								setError('');
							}}
							placeholder={exampleUrl}
							error={error}
						/>
						<p className={styles.urlHint}>{hint}</p>
					</div>
				</Modal.Content>
				<Modal.Footer>
					<Button variant="secondary" onClick={handleClose}>
						{t`Cancel`}
					</Button>
					<Button variant="primary" onClick={handleSubmit}>
						{isEditing ? t`Save` : t`Add`}
					</Button>
				</Modal.Footer>
			</Modal.Root>
		);
	},
);
